import React, { useState } from 'react';
import { Camera, Mic, FileText, HardDrive, TrendingUp, Users } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { StatCard } from '@/components/widgets/StatCard';
import { DataTable, Column } from '@/components/ui/DataTable';
import { TrendLineChart } from '@/components/charts/TrendLineChart';
import { useUsageSummary, useUsageByDay, useTopUsers } from '@/hooks/useUsage';
import type { TopUser } from '@/types/admin.types';

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('pt-BR').format(num);
};

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const UsageOverviewPage: React.FC = () => {
  const [period, setPeriod] = useState('30d');
  const { data: summary, isLoading: summaryLoading } = useUsageSummary(period);
  const { data: usageByDay, isLoading: chartLoading } = useUsageByDay(period);
  const { data: topUsers, isLoading: topUsersLoading } = useTopUsers(10, period);

  const topUsersColumns: Column<TopUser>[] = [
    {
      key: 'rank',
      label: '#',
      render: (_, index) => (
        <span className="text-gray-500">{(index ?? 0) + 1}</span>
      ),
    },
    {
      key: 'name',
      label: 'Usuario',
      render: (user) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{user.phone}</p>
        </div>
      ),
    },
    {
      key: 'photos',
      label: 'Fotos',
      align: 'center',
      render: (user) => formatNumber(user.photos),
    },
    {
      key: 'audios',
      label: 'Audios',
      align: 'center',
      render: (user) => formatNumber(user.audios),
    },
    {
      key: 'texts',
      label: 'Textos',
      align: 'center',
      render: (user) => formatNumber(user.texts),
    },
    {
      key: 'total',
      label: 'Total',
      align: 'center',
      render: (user) => (
        <span className="font-semibold">{formatNumber(user.total)}</span>
      ),
    },
  ];

  // Prepare chart data
  const chartData = usageByDay?.map((day) => ({
    date: new Date(day.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    Fotos: day.photos,
    Audios: day.audios,
    Textos: day.texts,
  })) || [];

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Uso do Sistema</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Metricas de uso e consumo de recursos
          </p>
        </div>
        <Select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          options={[
            { value: '7d', label: 'Ultimos 7 dias' },
            { value: '30d', label: 'Ultimos 30 dias' },
            { value: '90d', label: 'Ultimos 90 dias' },
          ]}
          className="w-48"
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total de Fotos"
          value={formatNumber(summary?.totalPhotos || 0)}
          icon={<Camera className="w-6 h-6" />}
          trend={summary?.photosTrend}
          description="Recibos processados"
        />
        <StatCard
          title="Total de Audios"
          value={formatNumber(summary?.totalAudios || 0)}
          icon={<Mic className="w-6 h-6" />}
          trend={summary?.audiosTrend}
          description="Transcricoes realizadas"
        />
        <StatCard
          title="Total de Textos"
          value={formatNumber(summary?.totalTexts || 0)}
          icon={<FileText className="w-6 h-6" />}
          trend={summary?.textsTrend}
          description="Mensagens processadas"
        />
        <StatCard
          title="Storage Usado"
          value={formatBytes(summary?.storageUsedBytes || 0)}
          icon={<HardDrive className="w-6 h-6" />}
          description="Armazenamento total"
        />
      </div>

      {/* Usage Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Uso por Dia
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartLoading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner />
            </div>
          ) : (
            <TrendLineChart
              data={chartData}
              xKey="date"
              lines={[
                { key: 'Fotos', color: '#3b82f6' },
                { key: 'Audios', color: '#10b981' },
                { key: 'Textos', color: '#f59e0b' },
              ]}
              height={300}
            />
          )}
        </CardContent>
      </Card>

      {/* Top Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Top Usuarios por Uso
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable
            data={topUsers || []}
            columns={topUsersColumns}
            keyExtractor={(user) => user.userId}
            isLoading={topUsersLoading}
            emptyMessage="Nenhum dado de uso encontrado"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default UsageOverviewPage;
